import { Component, computed, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { CommonModule } from '@angular/common';

import { ActionService } from '../../../../services/action.service';
import { DropdownObjectType } from '../../../../shared/types';
import { TaxonomyService } from '../../../../services/taxonomy.service';
import { CustomSelectComponent } from '../../../../components/custom-select/custom-select.component';
import { ConfirmPopupService } from '../../../../services/confirm-popup.service';
import { NotificationService } from '../../../../services/notification.service';
import { PopupConfirmationData } from '../../../../shared/constant';
import { IntentFormComponent } from './intent-form/intent-form.component';
import { RightPanelHeaderComponent } from '../../../../components/right-panel-header/right-panel-header.component';
import { CommonService } from '../../../../services/common.service';
import { delayLoading } from '../../../../shared/appUtil';
import { TaxonomyApiService } from '../../../../services/api-services/taxonomy-api.service';
import { LinkedItem, TaxonomyEntityFormGroup, TaxonomyRequest } from '../../taxonomy.model';
import { TaxonomyTypes } from '../../taxonomy.constants';

@Component({
  selector: 'app-entity-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, CustomSelectComponent, IntentFormComponent, RightPanelHeaderComponent],
  templateUrl: './entity-form.component.html',
  styleUrl: './entity-form.component.scss',
  animations: [
    trigger('contentAnimation', [
      state(
        'void',
        style({
          maxHeight: '0',
          opacity: 0,
          overflow: 'hidden',
        })
      ),
      state(
        '*',
        style({
          maxHeight: '600px',
          opacity: 1,
        })
      ),
      transition('void <=> *', animate('300ms ease-in-out')),
    ]),
  ],
})
export class EntityFormComponent {
  constructor(
    private fb: FormBuilder,
    public actionsService: ActionService,
    public taxonomyService: TaxonomyService,
    private taxonomyApiService: TaxonomyApiService,
    private confirmPopupService: ConfirmPopupService,
    public notificationService: NotificationService,
    private commonService: CommonService
  ) {
    this.entityForm = this.fb.group({
      type: [TaxonomyTypes[0].value, Validators.required],
      label: ['', Validators.required],
      description: ['', Validators.required],
    });
  }

  _newIntentForm = signal<boolean>(false);
  _intentUpdated = signal<boolean>(false);
  _editIntentId = signal<number>(-1);
  _deleteIntentId = signal<number>(-1);
  _selectedTaxonomyItem = computed<any>(() => this.taxonomyService.taxonomyItem());
  _typeContent = TaxonomyTypes;
  originalFormValues: TaxonomyEntityFormGroup | undefined;
  applyButtonEnabled: boolean = false;
  entityForm: FormGroup;
  isSectionOneOpen = true;
  isSectionTwoOpen = true;

  _selectedValue = this.taxonomyService.getSelectedValue();
  taxoSelectedValue = computed(() => this._selectedValue()?.taxonomy_name || 'Select Taxonomy');

  ngOnInit() {
    if (this.taxonomyService.entitityFormFlag == 'create') {
      this.entityForm.patchValue({
        label: '',
        type: 'Primary Topic',
        description: '',
      });
    }
    if (this.taxonomyService.entitityFormFlag == 'update') {
      this.getEnitityById();
    }
  }
  getEnitityById() {
    const body: any = this.taxonomyService.enitity;

    this.taxonomyApiService.getEnitityByIdV2(body).subscribe((res: any) => {
      this.taxonomyService.selectedEnitity = res.data;
      this.entityForm.patchValue({
        label: this.taxonomyService.selectedEnitity.EntityName,
        type: this.taxonomyService.selectedEnitity.EntityType,
        description: this.taxonomyService.selectedEnitity.EntityDescription,
      });
    });
  }
  checkFormChanges(): void {
    this.applyButtonEnabled = JSON.stringify(this.entityForm.value) !== JSON.stringify(this.originalFormValues);
  }

  toggleSectionTwo(event: MouseEvent) {
    this.isSectionTwoOpen = !this.isSectionTwoOpen;
    event.stopPropagation();
  }

  toggleSectionOne(event: MouseEvent) {
    this.isSectionOneOpen = !this.isSectionOneOpen;
    event.stopPropagation();
  }

  convertToTaxonomyRequest(input: any, taxoSelectedValue: any): TaxonomyRequest {
    return {
      taxonomy_id: taxoSelectedValue.taxonomy_id,
      entity_name: input.label,
      entity_type: input.type,
      entity_description: input.description,
      linked_items_type: input.linked_items_type || 'Intents',
      linked_entities:
        input.linked_items?.map((item: any) => ({
          label: item.label,
          description: item.description,
        })) ||
        input.LinkedEntities?.map((item: any) => ({
          label: item.label,
          description: item.description,
        })),
    };
  }

  async onApply() {
    let tempItem = {
      ...this.taxonomyService.selectedEnitity,
      ...this.entityForm.value,
    };
    let selectedTaxonomyId = this.taxonomyService.getSelectedValue;
    const updatedTax: any = this.convertToTaxonomyRequest(tempItem, this.taxonomyService.selectedTaxonomy);

    if (this.taxonomyService.entitityFormFlag == 'create') {
      this.taxonomyApiService.addEntityV2(updatedTax).subscribe(() => {});
    } else if (this.taxonomyService.entitityFormFlag == 'update') {
      const updateTaxonomyBody: any = updatedTax;
      updateTaxonomyBody.entity_id = this.taxonomyService.enitity?.entity_id;

      this.taxonomyApiService.updateEntityV2(updateTaxonomyBody).subscribe((res) => {
        this.notificationService.success('Update entity', 'Entity updated');
      });
    }

    await delayLoading(1);

    this.taxonomyService.triggerTaxonomyUpdate();
    this.actionsService.closeAddEntityForm();
  }

  onClose() {
    if (this.entityForm.valid && (this.applyButtonEnabled || this._intentUpdated())) {
      this.confirmPopupService
        .openConfirmDialog(PopupConfirmationData.cancelTaxonomyConfirmation)
        .subscribe((result) => {
          if (result?.data?.confirm) {
            this.actionsService.closeAddEntityForm();
          }
        });
    } else {
      this.actionsService.closeAddEntityForm();
    }
  }

  onDelete() {
    const body: any = this.taxonomyService.enitity;

    this.confirmPopupService.openConfirmDialog(PopupConfirmationData.deleteTaxonomyConfirmation).subscribe((result) => {
      if (result?.data?.confirm) {
        this.taxonomyApiService.deleteEntityV2(body).subscribe(async (res) => {
          await delayLoading(1);
          this.taxonomyService.triggerTaxonomyUpdate();
          this.notificationService.success('Delete item', 'Entity deleted');
        });
        this.actionsService.closeAddEntityForm();
      }
    });
  }

  handleAddNewIntent() {
    this._newIntentForm.set(true);
  }

  handleCancelIntent() {
    this._newIntentForm.set(false);
  }

  handleIntentFormComplete() {
    this._intentUpdated.set(true);
    this._editIntentId.set(-1);
    this._newIntentForm.set(false);
  }

  handleCancelEditIntent() {
    this._editIntentId.set(-1);
  }

  handleEditIntent(index: number) {
    this._editIntentId.set(index);
    this._deleteIntentId.set(-1);
  }

  handleDeleteIntent(index: number) {
    this._deleteIntentId.set(index);
  }

  handleDeleteIntentCancel() {
    this._deleteIntentId.set(-1);
  }

  handleDeleteIntentConfirm(item: LinkedItem) {
    if (this.taxonomyService.selectedEnitity.LinkedEntities) {
      this.taxonomyService.selectedEnitity.LinkedEntities?.splice(this._deleteIntentId(), 1);
      this._intentUpdated.set(true);
    }
    this.handleDeleteIntentCancel();
  }

  get type(): DropdownObjectType {
    return {
      value: this.entityForm.get('type')?.value,
      label: this.entityForm.get('type')?.value,
    } as DropdownObjectType;
  }

  handleOperatorSelect(value: DropdownObjectType) {
    this.entityForm.patchValue({
      type: value.value,
    });
  }
  ngOnDestroy() {
    this.taxonomyService.selectedEnitity = { LinkedEntities: [] };
  }
  handleAddIntent() {
    if (!(this.taxonomyService.selectedEnitity.LinkedEntities == null)) {
      this.taxonomyService.selectedEnitity.LinkedEntities.push({
        label: '',
        description: '',
      });
      return;
    }
    let arr: any = [];
    this.taxonomyService.selectedEnitity.LinkedEntities = arr;
  }
}
